import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
	clx,
	currencyFormatter,
	formatLocaleDateTime,
	getDiscountFormat
} from 'Utils';
import { Invoice } from 'Types';
import styles from 'partials/card/subscription-card.module.scss';
import { SkeletonDetailsInvoice } from '../skeleton/SkeletonDetailsInvoice';

interface Props {
	transaction: Invoice;
	isLoading: boolean;
}

export const DetailsInvoice: FC<Props> = ({ transaction, isLoading }) => {
	const { t } = useTranslation();
	const row_class = 'justify-content-between';

	return isLoading ? (
		<SkeletonDetailsInvoice />
	) : (
		<div>
			<h6 className="text-gray-3 mb-3 fs-6 fw-400 d-flex align-items-center justify-content-between">
				{t('title.invoice')}
			</h6>
			<div className={clx(styles.row, row_class)}>
				<span className={styles.rowLabel}>
					{t('label.invoice-number')}:
				</span>
				<span className="fs-6">
					{transaction?.invoiceInfo?.invoiceNumber ?? '---'}
				</span>
			</div>
			<div
				className={clx(styles.row, 'justify-content-between border-0')}>
				<span className={styles.rowLabel}>
					{t('label.created-at')}:
				</span>
				<span>
					{transaction?.createdAt
						? formatLocaleDateTime(transaction?.createdAt)
						: '---'}
				</span>
			</div>
			<hr className="bg-gray-2 mb-3" />
			<div className={clx(styles.row, row_class)}>
				<span className={styles.rowLabel}>{t('label.price')}:</span>
				<span>
					{currencyFormatter(
						transaction?.price?.subscriptionPrice ?? 0
					)}
				</span>
			</div>
			{transaction?.price?.discountAmount > 0 && (
				<div className={clx(styles.row, row_class)}>
					<div className={clx(styles.rowLabel, 'd-flex flex-column')}>
						<div className="d-flex align-items-center">
							{t('label.discount')}
							<span className="ms-1 text-gray-3 fs-small">
								(
								{getDiscountFormat(
									transaction?.price?.discountPercent
								)}
								)
							</span>
							:
						</div>
						{transaction?.price?.voucherCode && (
							<div className="d-flex align-items-center mt-1 lh-base text-gray-3 fs-small">
								{t('label.voucher-code')}:
								<span className="ms-1">
									{transaction?.price?.voucherCode}
								</span>
							</div>
						)}
					</div>
					<span>
						{transaction?.price?.discountAmount > 0
							? `- ${currencyFormatter(
									transaction?.price?.discountAmount
							  )}`
							: currencyFormatter(0)}
					</span>
				</div>
			)}
			<div className={clx(styles.row, row_class)}>
				<span className={styles.rowLabel}>{t('label.subtotal')}:</span>
				<span>
					{currencyFormatter(transaction?.price?.subTotal ?? 0)}
				</span>
			</div>
			<div className={clx(styles.row, row_class)}>
				<span className={styles.rowLabel}>
					{t('label.tax')}
					<span className="ms-1 text-gray-3 fs-small">
						({transaction?.price?.taxPercent ?? 0}%)
					</span>
					:
				</span>
				<span>
					{transaction?.price?.taxAmount > 0
						? `+ ${currencyFormatter(
								transaction?.price?.taxAmount
						  )}`
						: currencyFormatter(0)}
				</span>
			</div>
			<div className={clx(styles.row, row_class)}>
				<span className={clx(styles.rowLabel, 'fw-500')}>
					{t('label.total')}
				</span>
				<div className="d-flex flex-column text-end">
					<span className="fs-6 fw-500">
						{currencyFormatter(
							transaction?.price?.totalAmount ?? 0
						)}
					</span>
					<span className="text-gray-3 lh-base fs-small">
						{t('label.tax-included')}
					</span>
				</div>
			</div>
		</div>
	);
};
